import { useEffect, useMemo, useState } from 'react'
import { ArrowUpRight, PlayCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getAdminStats, getAdminAnalyticsOverview, type AdminStats, type AdminAnalyticsOverview } from '@/lib/api/admin'

export function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [overview, setOverview] = useState<AdminAnalyticsOverview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([getAdminStats(), getAdminAnalyticsOverview()])
      .then(([s, ov]) => {
        if (cancelled) return
        setStats(s)
        setOverview(ov)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load admin stats')
      })

    return () => {
      cancelled = true
    }
  }, [])

  const topSeries = useMemo(() => stats?.topSeries ?? [], [stats])
  const maxViews = useMemo(
    () => Math.max(0, ...topSeries.map((s) => s.totalViews || 0)),
    [topSeries],
  )

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Admin dashboard</h1>
          <p className="mt-1 text-sm text-gray-400">
            High-level snapshot of Binyuma TV activity, powered by live admin data from the backend.
          </p>
        </div>
        <Link
          to="/admin/content"
          className="inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#FFD700]"
        >
          Review pending shows
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Pending shows for approval
          </p>
          <p className="text-2xl font-semibold text_white mb-1">
            {stats ? stats.pendingShows : '—'}
          </p>
          <p className="text-xs text-gray-400">Creators are waiting for review</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Daily active users</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {overview ? overview.dailyActiveUsers.toLocaleString() : stats ? stats.activeUsers.toLocaleString() : '—'}
          </p>
          <p className="text-xs text-gray-400">Unique users with watch activity in last 24 hours</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">New signups (7 days)</p>
          <p className="text-2xl font-semibold text-white mb-1">
            {overview ? overview.newSignups7d.toLocaleString() : stats ? stats.newSignups7d : '—'}
          </p>
          <p className="text-xs text-gray-400">New accounts created in the last 7 days.</p>
        </div>
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black px-4 py-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Hours watched (7 days)
          </p>
          <p className="text-2xl font-semibold text-white mb-1">
            {overview
              ? overview.hoursWatched7d.toFixed(1)
              : stats
                ? stats.hoursWatched7d.toFixed(1)
                : '—'}
          </p>
          <p className="text-xs text-gray-400">
            Estimated hours of content watched in the last 7 days.
          </p>
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-red-700 bg-red-900/30 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      <section className="grid gap-6">
        <div className="rounded-xl border border-gray-800 bg-gradient-to-br from-gray-900/80 to-black p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-white">Top series by views</h2>
              <Link
                to="/admin/analytics"
                className="text-[11px] text-[#D4AF37] hover:text-[#FFD700] inline-flex items-center gap-1"
              >
                View analytics
                <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-gray-300">
              <PlayCircle className="w-3 h-3" />
              Based on total views
            </span>
          </div>
          {topSeries.length === 0 ? (
            <p className="text-xs text-gray-500">No series data available yet.</p>
          ) : (
            <div className="space-y-3">
              {topSeries.map((series) => {
                const widthPercent =
                  maxViews > 0 ? Math.max(8, Math.round((series.totalViews / maxViews) * 100)) : 0
                return (
                  <div
                    key={series.id}
                    className="rounded-lg border border-gray-800 bg-black/40 px-3 py-2 flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">{series.title}</p>
                      <p className="text-xs text-gray-400">{series.creator}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-1.5 rounded-full bg-gray-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#D4AF37]"
                          style={{ width: `${widthPercent}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-300">
                        {series.totalViews.toLocaleString()} views
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

